import type {
  StepItem,
  StepsLayout,
  StepsNumbering,
  StepsProps,
} from "@emkit/builder-widgets";
import { useEditor } from "../../store.js";
import { CheckboxField, ColorField, SelectField, TextField } from "./fields.js";
import type { WidgetInspectorProps } from "./registry.js";

const LAYOUT = ["horizontal", "vertical"] as const;
const NUMBERING = ["number", "icon", "none"] as const;

function newId(): string {
  return `st-${Math.random().toString(36).slice(2, 8)}`;
}

export function StepsInspector({ widget }: WidgetInspectorProps) {
  const update = useEditor((s) => s.updateWidgetProps);
  const p = widget.props as StepsProps;
  const items = p.items ?? [];

  const setItems = (next: StepItem[]) => update(widget.id, { items: next });
  const addItem = () =>
    setItems([...items, { id: newId(), title: "New step", description: "" }]);
  const updateItem = (id: string, patch: Partial<StepItem>) =>
    setItems(items.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  const removeItem = (id: string) => setItems(items.filter((it) => it.id !== id));
  const moveItem = (id: string, dir: "up" | "down") => {
    const idx = items.findIndex((it) => it.id === id);
    if (idx < 0) return;
    const target = dir === "up" ? idx - 1 : idx + 1;
    if (target < 0 || target >= items.length) return;
    const next = items.slice();
    const moved = next.splice(idx, 1)[0];
    if (!moved) return;
    next.splice(target, 0, moved);
    setItems(next);
  };

  return (
    <>
      <SelectField
        label="Layout"
        value={p.layout ?? "horizontal"}
        options={LAYOUT}
        onChange={(v) => update(widget.id, { layout: v as StepsLayout })}
      />
      <SelectField
        label="Numbering"
        value={p.numbering ?? "number"}
        options={NUMBERING}
        onChange={(v) => update(widget.id, { numbering: v as StepsNumbering })}
      />
      <CheckboxField
        label="Connector lines"
        value={p.connected ?? true}
        onChange={(v) => update(widget.id, { connected: v })}
      />
      <ColorField
        label="Accent"
        value={p.accent ?? "#111827"}
        onChange={(v) => update(widget.id, { accent: v })}
      />
      <ColorField
        label="Text"
        value={p.textColor ?? "#0f172a"}
        onChange={(v) => update(widget.id, { textColor: v })}
      />

      <div className="emke-section-title" style={{ marginTop: 8 }}>
        Steps ({items.length})
      </div>

      {items.map((it, i) => (
        <div
          key={it.id}
          style={{
            padding: "8px 16px 10px",
            borderBottom: "1px solid var(--border)",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: 4,
              justifyContent: "flex-end",
              marginBottom: 4,
            }}
          >
            <button
              type="button"
              className="emke-btn"
              onClick={() => moveItem(it.id, "up")}
              disabled={i === 0}
              style={{ padding: "2px 6px", opacity: i === 0 ? 0.4 : 1 }}
            >
              ↑
            </button>
            <button
              type="button"
              className="emke-btn"
              onClick={() => moveItem(it.id, "down")}
              disabled={i === items.length - 1}
              style={{
                padding: "2px 6px",
                opacity: i === items.length - 1 ? 0.4 : 1,
              }}
            >
              ↓
            </button>
            <button
              type="button"
              className="emke-btn is-danger"
              onClick={() => removeItem(it.id)}
              style={{ padding: "2px 6px" }}
            >
              ✕
            </button>
          </div>
          <TextField
            label={`Step ${i + 1} title`}
            value={it.title}
            onChange={(v) => updateItem(it.id, { title: v })}
          />
          <TextField
            label="Description"
            value={it.description ?? ""}
            multiline
            onChange={(v) => updateItem(it.id, { description: v || undefined })}
          />
          {p.numbering === "icon" && (
            <TextField
              label="Icon (glyph/emoji)"
              value={it.icon ?? ""}
              onChange={(v) => updateItem(it.id, { icon: v || undefined })}
            />
          )}
        </div>
      ))}

      <div style={{ padding: "12px 16px 0" }}>
        <button
          type="button"
          className="emke-btn"
          onClick={addItem}
          style={{ width: "100%" }}
        >
          + Add step
        </button>
      </div>
    </>
  );
}
